import { createClient, type Session, type SupabaseClient, type User } from "@supabase/supabase-js";
import { hasCloudRuntime, runtimeConfig } from "../config/runtimeConfig";
import { createSupabaseFetch } from "./supabaseCircuit";

let client: SupabaseClient | null | undefined;

export function getSupabaseClient(): SupabaseClient | null {
  if (client !== undefined) return client;
  client = hasCloudRuntime ? createClient(runtimeConfig.supabaseUrl, runtimeConfig.supabasePublishableKey, {
    global: { fetch: createSupabaseFetch(fetch.bind(globalThis), localStorage, `mvpstory:cloud-cooldown:${runtimeConfig.supabaseUrl}`) },
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  }) : null;
  return client;
}

export interface AccountSnapshot {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
  error?: string;
}

export function subscribeToAccount(listener: (snapshot: AccountSnapshot) => void): () => void {
  const supabase = getSupabaseClient();
  if (!supabase) {
    listener({ configured: false, loading: false, session: null, user: null });
    return () => undefined;
  }
  let active = true;
  listener({ configured: true, loading: true, session: null, user: null });
  void supabase.auth.getSession().then(({ data, error }) => {
    if (active) listener({
      configured: true,
      loading: false,
      session: data.session,
      user: data.session?.user ?? null,
      ...(error ? { error: error.message } : {}),
    });
  });
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    if (active) listener({ configured: true, loading: false, session, user: session?.user ?? null });
  });
  return () => { active = false; data.subscription.unsubscribe(); };
}

export function subscribeToAccountInvalidation(userId: string, onChange: () => void): () => void {
  const supabase = getSupabaseClient();
  if (!supabase || !userId) return () => undefined;
  const channel = supabase
    .channel(`account:${userId}`, { config: { private: true } })
    .on("broadcast", { event: "account_changed" }, onChange)
    .subscribe();
  return () => { void supabase.removeChannel(channel); };
}

export async function signInWithGoogle(): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error("ยังไม่ได้ตั้งค่าการเชื่อมต่อบัญชี");
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}${window.location.pathname}` },
  });
  if (error) throw error;
}

export async function signOutAccount(): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getFreshAccessToken(forceRefresh = false): Promise<string | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  try {
    if (forceRefresh) {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session?.access_token) {
        return data.session.access_token;
      }
    }
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      const expiresAt = data.session.expires_at;
      if (expiresAt && (expiresAt * 1000 - Date.now() < 60000)) {
        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.data.session?.access_token) {
          return refreshed.data.session.access_token;
        }
      }
      return data.session.access_token;
    }
  } catch (err) {
    console.warn("Failed to get fresh access token:", err);
  }
  return null;
}
